import {
  createFileRoute,
  useSearch,
  useNavigate,
} from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@/firebase/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Shield,
  Clock,
  Users,
  MapPin,
  DollarSign,
} from 'lucide-react';
import {
  type GetTripResponse,
  type CreatePaymentSessionResponse,
} from '../../../shared/types/payment';
import { SERVICE_TIERS } from '@/lib/service-tiers';
import { type Trip } from '../../../shared/types/trip';
import type { PaymentType } from '../../../shared/types/base';

export const Route = createFileRoute('/trip/payment')({
  component: TripPaymentPage,
  validateSearch: (search: Record<string, unknown>) => ({
    tripId: search.tripId as string,
    returnTo: search.returnTo as string,
  }),
});

const plans: Array<{
  type: PaymentType;
  label: string;
  price: string;
  period: string;
  scope: string;
  badge?: string;
}> = [
  {
    type: 'single_purchase',
    label: 'Single Trip',
    price: '$8',
    period: 'one-time',
    scope: 'This trip only',
  },
  {
    type: 'monthly',
    label: 'Monthly',
    price: '$11',
    period: '/mo',
    scope: 'All trips',
  },
  {
    type: 'annual',
    label: 'Annual',
    price: '$105',
    period: '/yr',
    scope: 'All trips',
    badge: 'Save 20%',
  },
];

function TripPaymentPage() {
  const navigate = useNavigate();
  const { tripId, returnTo } = useSearch({ from: '/trip/payment' });

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PaymentType>('single_purchase');

  useEffect(() => {
    const loadTrip = async () => {
      if (!tripId || !auth.currentUser) {
        setError('No trip ID provided or user not authenticated');
        setLoading(false);
        return;
      }

      try {
        const getTrip = httpsCallable(functions!, 'getTrip');
        const result = await getTrip({
          userId: auth.currentUser.uid,
          tripId,
        });

        const response = result.data as GetTripResponse;

        if (response.success && response.trip) {
          setTrip(response.trip);
        } else {
          setError(response.message || 'Failed to load trip');
        }
      } catch (err) {
        console.error('Error fetching trip:', err);
        setError('Failed to load trip details');
      } finally {
        setLoading(false);
      }
    };

    loadTrip();
  }, [tripId]);

  const handlePayment = async () => {
    if (!trip || !auth.currentUser) {
      setError('Trip data not available');
      return;
    }

    setProcessingPayment(true);
    setError(null);

    try {
      const createPaymentSession = httpsCallable(
        functions!,
        'createPaymentSession'
      );
      const result = await createPaymentSession({
        userId: auth.currentUser.uid,
        tripId,
        serviceTier: trip.preferences.serviceTier,
        paymentType: selectedPlan,
        returnUrl: `${window.location.origin}${returnTo}?tripId=${tripId}`,
        cancelUrl: `${window.location.origin}/trip/payment?tripId=${tripId}&returnTo=${returnTo}`,
      });

      const response = result.data as CreatePaymentSessionResponse;

      if (response.success && response.sessionUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.sessionUrl;
      } else {
        setError(response.message || 'Failed to create payment session');
      }
    } catch (err) {
      console.error('Error creating payment session:', err);
      setError('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream to-sand dark:from-dusk dark:to-dusk">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terra mx-auto mb-4"></div>
              <p className="text-warm-mid">
                Loading payment details...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream to-sand dark:from-dusk dark:to-dusk">
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto rounded-2xl border-sand bg-warm-white">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="h-12 w-12 text-rust mx-auto mb-4" />
              <p className="text-rust mb-4">{error}</p>
              <div className="space-x-2">
                <Button onClick={() => window.history.back()}>Go Back</Button>
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: '/trip/plan' })}
                >
                  Plan New Trip
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Find the service tier details
  const serviceTierDetails = SERVICE_TIERS.find(
    tier => tier.value === trip.preferences.serviceTier
  );

  if (!serviceTierDetails) {
    setError('Invalid service tier');
    return null;
  }

  const selectedPlanDetails = plans.find(p => p.type === selectedPlan)!;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-sand dark:from-dusk dark:to-dusk">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            className="mb-4 text-warm-mid hover:text-dusk"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <h1 className="text-3xl font-bold font-display text-dusk dark:text-warm-white mb-2">
            Complete Your Trip Payment
          </h1>
          <p className="text-warm-mid">
            Secure payment to unlock full trip editing and customization
            features
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Trip Summary */}
          <div className="space-y-6">
            <Card className="rounded-2xl border-sand bg-warm-white">
              <CardHeader>
                <CardTitle className="text-dusk font-display">Trip Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-terra" />
                    <div>
                      <p className="text-sm text-warm-mid">
                        Destination
                      </p>
                      <p className="font-medium text-dusk">
                        {trip.preferences.destination.city}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-clay" />
                    <div>
                      <p className="text-sm text-warm-mid">
                        Travelers
                      </p>
                      <p className="font-medium text-dusk">
                        {trip.preferences.travelers.adults} adults
                        {trip.preferences.travelers.children > 0 &&
                          `, ${trip.preferences.travelers.children} children`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-terra" />
                    <div>
                      <p className="text-sm text-warm-mid">
                        Duration
                      </p>
                      <p className="font-medium text-dusk">
                        {trip.itinerary.length || 'N/A'} days
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-terra" />
                    <div>
                      <p className="text-sm text-warm-mid">
                        Trip Budget
                      </p>
                      <p className="font-medium text-dusk">
                        ${trip.preferences?.budget?.total?.amount}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-sand">
                  <h4 className="font-medium text-dusk mb-2">Trip Includes:</h4>
                  <ul className="text-sm text-warm-mid space-y-1">
                    <li>• {trip.itinerary.length} accommodation options</li>
                    <li>• {trip.itinerary.length} travel options</li>
                    <li>
                      • {trip.itinerary.length} activities and attractions
                    </li>
                    <li>
                      • {trip.itinerary.length} restaurant recommendations
                    </li>
                    <li>• AI-generated personalized itinerary</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Security Notice */}
            <Alert className="rounded-2xl border-sand bg-warm-white">
              <Shield className="h-4 w-4 text-sage" />
              <AlertDescription className="text-warm-mid">
                Your payment is secured by Stripe. We never store your payment
                information.
              </AlertDescription>
            </Alert>
          </div>

          {/* Payment Section */}
          <div className="space-y-6">
            {/* Pricing Plan Selection */}
            <Card className="rounded-2xl border-sand bg-warm-white">
              <CardHeader>
                <CardTitle className="text-dusk font-display">Choose Your Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {plans.map(plan => (
                    <button
                      key={plan.type}
                      onClick={() => setSelectedPlan(plan.type)}
                      className={`relative rounded-xl border-2 p-3 text-left transition-colors focus:outline-none ${
                        selectedPlan === plan.type
                          ? 'border-terra bg-terra/5'
                          : 'border-sand bg-warm-white hover:border-terra/50'
                      }`}
                    >
                      {plan.badge && (
                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-sage px-2 py-0.5 text-[10px] font-semibold text-white">
                          {plan.badge}
                        </span>
                      )}
                      <p className="text-xs font-semibold text-dusk mb-1">
                        {plan.label}
                      </p>
                      <p className="text-lg font-black font-display text-terra leading-none">
                        {plan.price}
                        <span className="text-xs font-normal text-warm-mid ml-0.5">
                          {plan.period}
                        </span>
                      </p>
                      <p className="text-[11px] text-warm-mid mt-1">{plan.scope}</p>
                      {plan.type !== 'single_purchase' && (
                        <p className="text-[10px] text-warm-mid/70 mt-0.5">Cancel anytime</p>
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="border-2 border-terra/20 bg-cream dark:bg-dusk rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl text-dusk dark:text-warm-white font-display">
                    {serviceTierDetails.name}
                  </CardTitle>
                  {serviceTierDetails.popular && (
                    <Badge className="bg-terra/15 text-terra border-terra/40">
                      Most Popular
                    </Badge>
                  )}
                </div>
                <p className="text-warm-mid">
                  {serviceTierDetails.description}
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <h4 className="font-medium text-dusk dark:text-warm-white">What's Included:</h4>
                  <ul className="space-y-2">
                    {serviceTierDetails.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-warm-mid">
                        <CheckCircle className="h-4 w-4 text-sage mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Payment Button */}
            <Card className="rounded-2xl border-sand bg-warm-white">
              <CardContent className="pt-6">
                {error && (
                  <Alert className="mb-4" variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={handlePayment}
                  disabled={processingPayment}
                  className="w-full h-12 text-lg bg-terra hover:bg-rust text-white rounded-full"
                  size="lg"
                >
                  {processingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      {selectedPlan === 'single_purchase'
                        ? `Pay $8 - Secure Checkout`
                        : selectedPlan === 'monthly'
                          ? `Subscribe $11/mo - Secure Checkout`
                          : `Subscribe $105/yr - Secure Checkout`}
                    </>
                  )}
                </Button>

                <div className="mt-4 text-center">
                  <p className="text-xs text-warm-mid/60">
                    By proceeding, you agree to our Terms of Service and Privacy
                    Policy. You'll be redirected to Stripe for secure payment
                    processing.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card className="rounded-2xl border-sand bg-warm-white">
              <CardContent className="pt-6">
                <h4 className="font-medium text-dusk mb-3">Accepted Payment Methods</h4>
                <div className="flex space-x-2 opacity-60">
                  <div className="bg-sand dark:bg-warm-mid/20 px-3 py-1 rounded-full text-xs text-warm-mid">
                    Visa
                  </div>
                  <div className="bg-sand dark:bg-warm-mid/20 px-3 py-1 rounded-full text-xs text-warm-mid">
                    Mastercard
                  </div>
                  <div className="bg-sand dark:bg-warm-mid/20 px-3 py-1 rounded-full text-xs text-warm-mid">
                    Amex
                  </div>
                  <div className="bg-sand dark:bg-warm-mid/20 px-3 py-1 rounded-full text-xs text-warm-mid">
                    PayPal
                  </div>
                  <div className="bg-sand dark:bg-warm-mid/20 px-3 py-1 rounded-full text-xs text-warm-mid">
                    Apple Pay
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TripPaymentPage;
